import type { Capability, EntitlementValue, ScopeRef } from "@enduring-systems/entitlements";
import { validateValueForType } from "@enduring-systems/entitlements";
import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import { Label } from "@/common/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/common/ui/select";

import { EntitlementControl } from "./EntitlementControl";
import {
  clearGrant,
  type GrantedAtScope,
  type GrantMutationResult,
  type GrantTier,
  setGrant,
} from "./entitlements-api";
import { rawValue } from "./scope";

export interface GrantEditorProps {
  readonly scope: ScopeRef | null;
  readonly capabilities: readonly Capability[];
  /** Owned by the page so the resolution view always describes the same capability. */
  readonly capabilityId: string;
  readonly onCapabilityChange: (capabilityId: string) => void;
  /** Fired after a write lands, so the resolution view can re-read what now applies. */
  readonly onMutated: () => void;
  /**
   * What is currently set at this scope, from the page's reading. The control seeds
   * from this rather than the capability default so an unedited Save cannot quietly
   * write the default over an existing grant.
   */
  readonly granted: GrantedAtScope | null;
  /**
   * Whether `granted` is an answer at all. `null` means "nothing is set here" ONLY
   * when the read succeeded; while it is in flight or after it failed, `granted` is
   * also null but unknown, and seeding the default off that is how the overwrite
   * this prop exists to prevent comes back through the error path.
   */
  readonly grantedKnown: boolean;
}

const TIERS: readonly GrantTier[] = ["base", "override"];

/**
 * Picks a capability, edits its value with the type-appropriate control (ENR-1128),
 * and saves/clears the grant at the selected scope through the identity BFF. The
 * admin-api's staff guard authorizes the write; a validation error (e.g. quota < 0)
 * comes back as a typed message and blocks nothing client-side beyond showing it.
 */
export function GrantEditor({
  scope,
  capabilities,
  capabilityId,
  onCapabilityChange,
  onMutated,
  granted,
  grantedKnown,
}: GrantEditorProps): JSX.Element {
  const [tier, setTier] = useState<GrantTier>("base");
  const [value, setValue] = useState<EntitlementValue | null>(null);
  const [result, setResult] = useState<GrantMutationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const capability = capabilities.find((candidate) => candidate.id === capabilityId) ?? null;

  /**
   * Seed the editor from what is actually set at the scope, falling back to the
   * capability default only when the read came back saying nothing is set (or the
   * stored value no longer validates). Seeding from the default while the panel
   * alongside displays a real grant is how an unedited Save writes 0 over an
   * existing "unlimited"; doing it while the answer is merely UNKNOWN is the same
   * bug reached through a reload or a failed read.
   *
   * `tier` and `value` are the only two fields the operator submits that live here
   * (scope and capability are owned by the page), and BOTH are seeded here. Seeding
   * one without the other is its own bug: an override's value shown against tier
   * `base` writes that value to the wrong tier. Any field added to this form belongs
   * in this effect.
   */
  useEffect(() => {
    if (!capability || !grantedKnown) {
      setValue(null);
      return;
    }
    if (granted === null) {
      setTier("base");
      setValue(capability.defaultValue);
      return;
    }
    setTier(granted.tier);
    const validated = validateValueForType(capability.type, granted.value);
    setValue(validated.ok ? validated.value : capability.defaultValue);
  }, [capability, granted, grantedKnown]);

  // A write's outcome describes the scope it was made at, so it cannot outlive a
  // scope change and sit beside a panel describing somewhere else.
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [scope]);

  function selectCapability(id: string): void {
    onCapabilityChange(id);
    setResult(null);
    setError(null);
  }

  async function run(action: () => Promise<GrantMutationResult>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setResult(await action());
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = scope !== null && capability !== null && !busy && grantedKnown;

  return (
    <div className="space-y-4" data-testid="grant-editor">
      <div className="space-y-1.5">
        <Label htmlFor="capability">Capability</Label>
        <Select value={capabilityId} onValueChange={selectCapability} disabled={busy}>
          <SelectTrigger id="capability" data-testid="capability-select">
            <SelectValue placeholder="Select a capability" />
          </SelectTrigger>
          <SelectContent>
            {capabilities.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tier">Tier</Label>
        <Select value={tier} onValueChange={(next) => setTier(next as GrantTier)} disabled={busy}>
          <SelectTrigger id="tier" data-testid="tier-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIERS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {capability && value && (
        <div className="space-y-1.5">
          <Label htmlFor={`${capability.id}-${capability.type.kind}`}>Value</Label>
          <EntitlementControl
            capabilityId={capability.id}
            type={capability.type}
            value={value}
            onChange={setValue}
            disabled={busy}
          />
        </div>
      )}

      <div className="flex gap-2">
        <Button
          data-testid="save-grant"
          disabled={!canSubmit || value === null}
          onClick={() => {
            if (scope && capability && value) {
              void run(() =>
                setGrant({
                  scope,
                  capabilityId: capability.id,
                  tier,
                  value: rawValue(value),
                })
              );
            }
          }}
        >
          Save grant
        </Button>
        <Button
          variant="outline"
          data-testid="clear-grant"
          disabled={!canSubmit}
          onClick={() => {
            if (scope && capability) {
              void run(() => clearGrant({ scope, capabilityId: capability.id, tier }));
            }
          }}
        >
          Clear grant
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive" data-testid="grant-error">
          {error}
        </p>
      )}
      {result && (
        <p className="text-sm text-muted-foreground" data-testid="grant-result">
          {result.after === null
            ? `Cleared ${result.capabilityId} (${result.tier}).`
            : `Set ${result.capabilityId} (${result.tier}).`}{" "}
          Blast radius: {result.blastRadius}.
        </p>
      )}
    </div>
  );
}
